import { IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { IPartnerGroupInfo } from 'legacy/lut/classes/models/partner360/IPartnerGroup'
import { fetchPartnerGroup } from 'legacy/lut/components/widgets/public/partner360/ELT/api/partnerGroup/fetchPartnerGroup'
import { fetchPartnerGroupByUID } from 'legacy/lut/components/widgets/public/partner360/ELT/api/partnerGroup/fetchPartnerGroupByUID'
import { fetchPartnerGroups } from 'legacy/lut/components/widgets/public/partner360/ELT/api/partnerGroup/fetchPartnerGroups'
import {
  GROUP_ID_VERSION_PARAMETER,
  PARTNER_ID_VERSION_PARAMETER,
  URL_VERSION_PARAMETER,
} from 'legacy/lut/components/widgets/public/partner360/ELT/configs'
import { getPartnerGroupData } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/partnerGroups/getPartnerGroupData'
import { IFetchFormDataArgs } from 'legacy/lut/components/widgets/public/partner360/ELT/models/IFetchFormDataArgs'
import { IPartnerGroupAdditionalInfo } from 'legacy/lut/components/widgets/public/partner360/ELT/models/IPartnerGroupAdditionalInfo'
import { getVersionByVersionUID } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/versions/helpers/getVersionByVersionUID'
import { TSelectVersionMode } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/versions/types/Types'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { deleteParamsFromUrl } from 'core-components/helpers/url'

interface IUseEltRouting {
  versionId: string
  urlParameterForGroupVersions: string
  urlParameterForPartnerVersions: string
  data?: IELTData
  partnerId: string
  showVersionsHandler: boolean
  selectVersion: (
    mode: TSelectVersionMode
  ) => (
    selectedVersion: IELTData,
    versionName?: string,
    versionSerialNumber?: number
  ) => Promise<void>
  fetch: (params: IFetchFormDataArgs) => void
  setPartnerGroupInfo: (data: IPartnerGroupAdditionalInfo) => void
  setIsInitialLoading: (data: boolean) => void
  setPartnerGroups: (data: IPartnerGroupInfo[]) => void
}

export const useEltRouting = ({
  data,
  versionId,
  urlParameterForPartnerVersions,
  urlParameterForGroupVersions,
  partnerId,
  showVersionsHandler,
  selectVersion,
  fetch,
  setPartnerGroupInfo,
  setIsInitialLoading,
  setPartnerGroups,
}: IUseEltRouting) => {
  const navigate = useNavigate()

  const fetchGroupInfo = useCallback(async (groupId: number) => {
    const group = await fetchPartnerGroup(groupId)
    return group ? getPartnerGroupData(group) : null
  }, [])

  const handleAllVersionsFetch = useCallback(
    async (partnerId: string) => {
      const partnerGroups = await fetchPartnerGroups(partnerId)

      if (partnerGroups.length) {
        setPartnerGroups(partnerGroups)
        setIsInitialLoading(false)
      } else {
        fetch({ partnerId })
      }
    },
    [fetch, setIsInitialLoading, setPartnerGroups]
  )

  const handleVersionFetch = useCallback(
    async (versionId: string, partnerId: string) => {
      const version = await getVersionByVersionUID(versionId)

      if (!version) {
        navigate(deleteParamsFromUrl([URL_VERSION_PARAMETER]))
        handleAllVersionsFetch(partnerId)
        return
      }

      if (version.GroupId) {
        const groupInfo = await fetchGroupInfo(version.GroupId)
        if (groupInfo) {
          setPartnerGroupInfo(groupInfo)
        }
      }

      selectVersion('change')(version)
    },
    [fetchGroupInfo, handleAllVersionsFetch, navigate, selectVersion, setPartnerGroupInfo]
  )

  const handleGroupVersionFetch = useCallback(
    async (groupVersionUID: string, partnerId: string) => {
      const groupData = await fetchPartnerGroupByUID(groupVersionUID)

      if (!groupData) {
        navigate(deleteParamsFromUrl([GROUP_ID_VERSION_PARAMETER]))
        handleAllVersionsFetch(partnerId)
        return
      }

      fetch({ groupData, partnerId })
    },
    [fetch, handleAllVersionsFetch, navigate]
  )

  const fetchEltData = useCallback(async () => {
    if (showVersionsHandler) {
      return
    }
    if (versionId && !data?.EntryId) {
      await handleVersionFetch(versionId, partnerId)
      return
    }

    if (urlParameterForGroupVersions && !data?.EntryId) {
      await handleGroupVersionFetch(urlParameterForGroupVersions, partnerId)
      return
    }

    if (urlParameterForPartnerVersions && !data?.EntryId) {
      if (partnerId === urlParameterForPartnerVersions) {
        fetch({ partnerId })
        return
      } else {
        navigate(deleteParamsFromUrl([PARTNER_ID_VERSION_PARAMETER]))
      }
    }

    if (!showVersionsHandler && !data?.EntryId) {
      await handleAllVersionsFetch(partnerId)
    }
  }, [
    versionId,
    data?.EntryId,
    urlParameterForGroupVersions,
    urlParameterForPartnerVersions,
    partnerId,
    showVersionsHandler,
    handleVersionFetch,
    handleGroupVersionFetch,
    fetch,
    navigate,
    handleAllVersionsFetch,
  ])

  return { fetchEltData }
}
